import { useMemo } from "react";

import type { OutgoingIntakeFormNode } from "@/api/types/outgoingIntake";
import type { LibrarySource } from "@/features/intakeBuilder";

import { useRequestsData } from "../data/RequestsDataContext";
import { isUserFacingTemplate } from "../model";

/** The catalog templates whose questions make up the question bank. */
const BANK_TEMPLATES = [
  "biographical",
  "address_history",
  "employment_history",
  "immigration_history",
  "dependents",
  "documents"
];

const questionsOf = (
  nodes: OutgoingIntakeFormNode[]
): OutgoingIntakeFormNode[] => {
  const out: OutgoingIntakeFormNode[] = [];
  const walk = (list: OutgoingIntakeFormNode[]) => {
    for (const n of list) {
      if (n.node_type === "question") out.push(n);
      else if (n.children) walk(n.children);
    }
  };
  walk(nodes);
  return out;
};

/** "From Library" for the Requests builder: the question bank and the USCIS forms. */
export const useRequestsLibrary = (): LibrarySource => {
  const data = useRequestsData();
  return useMemo(
    () => ({
      bank: BANK_TEMPLATES.map((id) => data.templateById(id))
        .filter((t): t is NonNullable<typeof t> => Boolean(t))
        .map((t) => ({
          id: t.template_id,
          label: t.label,
          questions: questionsOf(t.schema ?? [])
        })),
      forms: data.templates.filter(isUserFacingTemplate).map((t) => ({
        id: t.template_id,
        label: t.label,
        meta: `${t.question_count ?? 0} questions`
      })),
      ensureLoaded: () => void data.ensureTemplateSchemas(BANK_TEMPLATES)
    }),
    [data]
  );
};
